import { Lock, KeyRound, Eye, Clock, ShieldCheck, ShieldQuestion, Dice5, ServerCog, MessageSquare } from "lucide-react";
import { Page, H1, H2, Lead, P, UL, InfoCard, Notice, Toc } from "../public/primitives";
import i18n from "../../i18n";
import { useTranslation } from "react-i18next";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

// Tableau de conservation des données (durées confirmées ; sinon marqué "à définir avant production").
const RETENTION = [
  { get data() { return tr("public:security.secretAnonymeContenuChiffre"); }, get dur() { return tr("public:security.jusquALectureOu"); } },
  { get data() { return tr("public:security.secureRequestAnonymeContenu"); }, get dur() { return tr("public:security.jusquARecuperationOu"); } },
  { get data() { return tr("public:security.evenementsAntiAbusPseudonymises"); }, get dur() { return tr("public:security.dur48h"); } },
  { get data() { return tr("public:security.historiqueWorkspaceFree"); }, get dur() { return tr("public:security.durDays", { count: 30 }); } },
  { get data() { return tr("public:security.historiqueWorkspacePro"); }, get dur() { return tr("public:security.durDays", { count: 365 }); } },
  { get data() { return tr("public:security.historiqueWorkspaceTeam"); }, get dur() { return tr("public:security.durDays", { count: 365 }); } },
  { get data() { return tr("public:security.historiqueWorkspaceBusiness"); }, get dur() { return tr("public:security.durDays", { count: 730 }); } },
  { get data() { return tr("public:security.historiqueWorkspaceEnterprise"); }, get dur() { return tr("public:security.durDays", { count: 730 }); } },
];

export const SecurityPage = () => {
  useTranslation(); // re-rendu au changement de langue
  return (
  <Page testid="security-page">
    <H1>{tr("public:security.securiteCentreDeConfiance")}</H1>
    <Lead>
      {tr("public:security.cettePageDecritDe")}
    </Lead>

    <Toc
      items={[
        { id: "zero-knowledge", label: "Zero-Knowledge" },
        { id: "lecture-unique", label: tr("public:security.lectureUnique") },
        { id: "expiration", label: tr("public:security.expiration") },
        { id: "secure-request", label: tr("public:security.demandeSecurisee") },
        { id: "remise-sms", label: tr("public:security.remiseParSms") },
        { id: "fichiers-securises", label: tr("public:security.sfTitle") },
        { id: "protection-abus", label: tr("public:security.protectionContreLesAbus") },
        { id: "securite-http", label: tr("public:security.securiteHttp") },
        { id: "conservation", label: tr("public:security.conservationDesDonnees") },
        { id: "test-mdp", label: tr("public:security.testDeMotDe") },
        { id: "certifications", label: tr("public:security.certifications") },
      ]}
    />

    <div className="grid sm:grid-cols-2 gap-4 mt-6">
      <InfoCard icon={Lock} title={tr("public:security.chiffrementDansLeNavigateur")} testid="sec-card-encryption">
        {tr("public:security.leContenuSensibleEst")}
      </InfoCard>
      <InfoCard icon={KeyRound} title={tr("public:security.cleSepareeDuContenu")} testid="sec-card-key">
        {tr("public:security.laCleDeDechiffrement")}
      </InfoCard>
      <InfoCard icon={Eye} title={tr("public:security.lectureUnique")} testid="sec-card-oneread">
        {tr("public:security.apresUneRevelationReussie")}
      </InfoCard>
      <InfoCard icon={Clock} title={tr("public:security.expirationAutomatique")} testid="sec-card-expiration">
        {tr("public:security.unLienDevientAutomatiquement")}
      </InfoCard>
      <InfoCard icon={ShieldQuestion} title={tr("public:security.demandeSecurisee")} testid="sec-card-sr">
        {tr("public:security.uneDemandeUtiliseUne")}
      </InfoCard>
      <InfoCard icon={MessageSquare} title={tr("public:security.remiseParSms")} testid="sec-card-sms">
        {tr("public:security.leSmsNeContient")}
      </InfoCard>
      <InfoCard icon={Dice5} title={tr("public:security.generationSansBiais")} testid="sec-card-random">
        {tr("public:security.lesValeursCryptographiquesProviennent")}
      </InfoCard>
    </div>

    <H2 id="zero-knowledge">{tr("public:security.zeroKnowledge")}</H2>
    <P>
      {tr("public:security.securepassChiffreVosInformations")}
    </P>
    <Notice tone="emerald" testid="sec-zk-note">
      {tr("public:security.dansLesParcoursZero")}
    </Notice>

    <H2 id="lecture-unique">{tr("public:security.lectureUnique")}</H2>
    <P>
      {tr("public:security.apresUneRevelationReussie2")} <b>{tr("public:security.metadonnees")}</b> {tr("public:security.dateStatutPeuventRester")}
    </P>

    <H2 id="expiration">{tr("public:security.expiration")}</H2>
    <UL>
      <li>{tr("public:security.usageAnonyme4Heures")}</li>
      <li>{tr("public:security.compteFree24Heures")}</li>
      <li>{tr("public:security.offresPayantesSelonL")}</li>
    </UL>

    <H2 id="secure-request">{tr("public:security.demandeSecuriseeSecureRequest")}</H2>
    <P>
      {tr("public:security.leDemandeurGenereUne")}
    </P>

    <H2 id="remise-sms">{tr("public:security.remiseSecuriseeParSms")}</H2>
    <P>
      {tr("public:security.lorsDUnEnvoi")}
    </P>
    <UL>
      <li>{tr("public:security.leContenuEstChiffre")}</li>
      <li>{tr("public:security.leDeverrouillageEstRealise")}</li>
      <li>{tr("public:security.apres5CodesIncorrects")}</li>
      <li>{tr("public:security.validiteCourte10Minutes")}</li>
      <li>{tr("public:security.serviceActuellementEnPhase")}</li>
    </UL>

    <H2 id="fichiers-securises">{tr("public:security.sfTitle")}</H2>
    <P>{tr("public:security.sfIntro")}</P>
    <UL>
      <li>{tr("public:security.sfAes")}</li>
      <li>{tr("public:security.sfManifest")}</li>
      <li>{tr("public:security.sfKey")}</li>
      <li>{tr("public:security.sfStorage")}</li>
      <li>{tr("public:security.sfOneTime")}</li>
      <li>{tr("public:security.sfNoScan")}</li>
    </UL>

    <H2 id="protection-abus">{tr("public:security.protectionContreLesAbus")}</H2>
    <P>
      {tr("public:security.lesInformationsTechniquesNecessaires")}
    </P>

    <H2 id="securite-http">{tr("public:security.securiteHttp")}</H2>
    <UL>
      <li>{tr("public:security.https")}</li>
      <li>{tr("public:security.hstsEnProduction")}</li>
      <li><code>{tr("public:security.referrerPolicyNoReferrer")}</code>.</li>
      <li><code>{tr("public:security.cacheControlNoStore")}</code> {tr("public:security.surLesApiSensibles")}</li>
      <li><code>{tr("public:security.xContentTypeOptions")}</code>.</li>
      <li><code>{tr("public:security.xFrameOptions")}</code> / CSP <code>frame-ancestors 'none'</code>.</li>
    </UL>

    <H2 id="conservation">{tr("public:security.conservationDesDonnees")}</H2>
    <P>
      {tr("public:security.nousDistinguonsLe")} <b>{tr("public:security.contenuChiffre")}</b> {tr("public:security.payloadDes")} <b>{tr("public:security.metadonneesDHistorique")}</b>{tr("public:security.apresLectureLeContenu")}
    </P>
    <div data-testid="sec-retention-table" className="overflow-x-auto rounded-xl border border-slate-200 bg-white my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left">
            <th className="py-2.5 px-4 font-semibold text-slate-700">{tr("public:security.donnee")}</th>
            <th className="py-2.5 px-4 font-semibold text-slate-700">{tr("public:security.conservation")}</th>
          </tr>
        </thead>
        <tbody>
          {RETENTION.map((r) => (
            <tr key={r.data} className="border-t border-slate-100">
              <td className="py-2.5 px-4 text-slate-700">{r.data}</td>
              <td className="py-2.5 px-4 text-slate-600">{r.dur}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <P>
      {tr("public:security.pourTouteDureeNon")}
    </P>

    <H2 id="test-mdp">{tr("public:security.testDeMotDe")}</H2>
    <P>
      {tr("public:security.lAnalyseDeRobustesse")}
    </P>

    <H2 id="certifications">{tr("public:security.certifications")}</H2>
    <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="sec-certifications">
      <ServerCog className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
      <p className="text-sm text-slate-600 leading-relaxed">
        {tr("public:security.securepassNeRevendiqueAucune")}
      </p>
    </div>

    <details className="mt-8 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3" data-testid="sec-technical-details">
      <summary className="text-sm font-medium text-slate-600 cursor-pointer">{tr("public:security.pourLesUtilisateursTechniques")}</summary>
      <p className="text-xs text-slate-500 leading-relaxed mt-2">
        {tr("public:security.secretsAesGcm256")}
      </p>
    </details>
  </Page>
  );
};

export default SecurityPage;
